import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@shared/api/client';
import { adminUpdateScopeForm } from '@shared/api/scope-form';
import type { ScopeFormResponse, Uuid } from '@shared/types/api';

type Props = {
  open: boolean;
  onClose: () => void;
  transactionId: Uuid;
  scopeForm: ScopeFormResponse;
  onFinalized?: () => void;
};

export function AdminScopeFormReviewDialog({
  open,
  onClose,
  transactionId,
  scopeForm,
  onFinalized,
}: Props) {
  const [platformFeeRupees, setPlatformFeeRupees] = useState(
    scopeForm.platformFeeRupees || '',
  );
  const [serviceDescription, setServiceDescription] = useState(
    scopeForm.serviceDescription || '',
  );
  const [workScope, setWorkScope] = useState(scopeForm.workScope || '');
  const [deliverables, setDeliverables] = useState(scopeForm.deliverables || '');
  const [professionalFeeRupees, setProfessionalFeeRupees] = useState(
    scopeForm.professionalFeeRupees || '',
  );
  const [expectedTat, setExpectedTat] = useState(scopeForm.expectedTat || '');
  const [governmentFeeRupees, setGovernmentFeeRupees] = useState(
    scopeForm.governmentFeeRupees || '0',
  );

  const qc = useQueryClient();

  const submit = useMutation({
    mutationFn: () =>
      adminUpdateScopeForm(transactionId, {
        platformFeeRupees: platformFeeRupees.trim(),
        serviceDescription: serviceDescription.trim(),
        workScope: workScope.trim(),
        deliverables: deliverables.trim(),
        professionalFeeRupees: professionalFeeRupees.trim(),
        expectedTat: expectedTat.trim(),
        governmentFeeRupees: governmentFeeRupees.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scope-form', transactionId] });
      qc.invalidateQueries({ queryKey: ['admin', 'tx', transactionId] });
      qc.invalidateQueries({ queryKey: ['admin', 'tx'] });
      qc.invalidateQueries({ queryKey: ['admin', 'chat', transactionId, 'history'] });
      onFinalized?.();
      onClose();
    },
  });

  const totalCalc = (
    (Number(professionalFeeRupees) || 0) +
    (Number(platformFeeRupees) || 0) +
    (Number(governmentFeeRupees) || 0)
  ).toFixed(2);

  const isValid = platformFeeRupees.trim().length > 0 && Number(platformFeeRupees) >= 0;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (submit.isPending) return;
        onClose();
      }}
      title="Review Scope Form & Set Platform Fee"
      className="max-w-xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submit.isPending}>
            Cancel
          </Button>
          <Button disabled={!isValid || submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? 'Finalizing…' : 'Finalize & Send Quotation to Client'}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          Review the scope proposed by Associate. Enter the <strong>Platform Fee</strong> below to generate the Final Quotation for the client.
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-brand-50/50 p-3 rounded-md border border-brand-200">
          <div>
            <label className="block text-xs font-semibold text-brand-900 mb-1">
              Platform Fee (₹) *
            </label>
            <Input
              inputMode="decimal"
              value={platformFeeRupees}
              onChange={(e) => setPlatformFeeRupees(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="e.g. 500.00"
              className="bg-white font-medium border-brand-300 focus:border-brand-600"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Final Quotation Grand Total (₹)
            </label>
            <div className="h-9 flex items-center px-3 font-mono font-bold text-base text-gray-900 bg-white rounded border border-gray-300">
              ₹{totalCalc}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Service Description</label>
          <textarea
            value={serviceDescription}
            onChange={(e) => setServiceDescription(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-gray-300 p-2 text-xs focus:border-brand-600 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Work Scope</label>
          <textarea
            value={workScope}
            onChange={(e) => setWorkScope(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-gray-300 p-2 text-xs focus:border-brand-600 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Deliverables</label>
          <textarea
            value={deliverables}
            onChange={(e) => setDeliverables(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-gray-300 p-2 text-xs focus:border-brand-600 focus:outline-none"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Professional Fee (₹)
            </label>
            <Input
              inputMode="decimal"
              value={professionalFeeRupees}
              onChange={(e) => setProfessionalFeeRupees(e.target.value.replace(/[^0-9.]/g, ''))}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Government Fee (₹)</label>
            <Input
              inputMode="decimal"
              value={governmentFeeRupees}
              onChange={(e) => setGovernmentFeeRupees(e.target.value.replace(/[^0-9.]/g, ''))}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Expected TAT</label>
            <Input value={expectedTat} onChange={(e) => setExpectedTat(e.target.value)} />
          </div>
        </div>

        {submit.isError ? (
          <p className="text-sm text-red-600">{apiErrorMessage(submit.error)}</p>
        ) : null}
      </div>
    </Dialog>
  );
}
